import { useState, useEffect } from "react";
import { C, S, G, uid, today, fmtDate } from "../../utils/theme";
import { Modal, Sel, Txtarea, Badge, PageHdr, Empty, GlassCard } from "../../components/UI";
import { academicAPI, commAPI } from "../../utils/api";
import { isMyCourse } from "./StudentPages";



export default function StudentQA({ user }) {
  const [courses, setCourses] = useState([]);
  const [threads, setThreads] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ question: "", courseId: "" });
  const [aiLoading, setAiLoading] = useState(null);
  const [aiError, setAiError] = useState("");

  const load = async () => {
    try {
      const [c, q] = await Promise.all([academicAPI.getCourses(), commAPI.getQA()]);
      const levelRank = { general:0, core:1, specialist:2 };
      const myCourses = user.program_id
        ? (c.data||[]).filter(x => isMyCourse(x, user))
            .sort((a,b) => (levelRank[a.level]??9)-(levelRank[b.level]??9) || (a.order??1)-(b.order??1))
        : [];
      setCourses(myCourses);
      setThreads(q.data||[]);
    } catch(e) { console.error(e); }
  };

  useEffect(() => { load(); }, []);



  const postQuestion = async () => {
    if (!form.question.trim() || !form.courseId) return;
    try {
      const res = await commAPI.postQuestion({ course_id: form.courseId, question: form.question.trim() });
      setModal(false);
      setForm({ question: "", courseId: "" });
      await load();
      // Auto AI reply if key exists
      if (res.data) {
  getAIReply(res.data);
}
    } catch(e) { alert(e.message); }
  };

  const getAIReply = async (thread) => {
  setAiLoading(thread.id);
  setAiError("");
  try {
    await commAPI.askAI(thread.id);
    load();
  } catch(err) { setAiError(err.message); }
  finally { setAiLoading(null); }
};

  const myCourseIds = courses.map(c => c.id);
  const myThreads = threads.filter(q => myCourseIds.includes(q.course_id) && q.student_id === user.id).slice().reverse();

  return (
    <div>
      <PageHdr title="Q&A / AI Help" sub="Ask questions — get instant AI answers"
        action={
          <div style={{ display: "flex", gap: 10 }}>
            <button style={S.btn("primary")} onClick={() => { setForm({ question: "", courseId: courses[0]?.id || "" }); setModal(true); }}>
              ❓ Ask Question
            </button>
          </div>
        }
      />

      <div style={{ ...S.alert("info"), marginBottom: 22 }}>
  <><strong>🤖 AI-Powered Q&A:</strong> Ask any question — Claude AI will instantly answer!</>
</div>

      {aiError && <div style={{ ...S.alert("danger"), marginBottom: 16 }}>⚠️ {aiError}</div>}

      {myThreads.length === 0
        ? <Empty icon="💬" text="No questions yet" sub="Ask your first question!" />
        : myThreads.map(q => {
          const course = courses.find(c => c.id === q.course_id);
          const isOwn = q.student_id === user.id;
          const isLoading = aiLoading === q.id;

          return (
            <div key={q.id} style={{ ...S.card, marginBottom: 16 }}>
              <div style={{ padding: "18px 22px" }}>
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <div style={{ width: 38, height: 38, borderRadius: "50%", background: G.purple, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>
                    {isOwn ? "👤" : "👥"}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
                      <span style={{ fontWeight: 700, fontSize: 14, color: C.text }}>
                        {isOwn ? "You" : (q.student_name || "Student")}
                      </span>
                      {course && <Badge type={course.level === "general" ? "info" : course.level === "core" ? "purple" : "gold"}>{course.name}</Badge>}
                      <span style={{ fontSize: 12, color: C.textMuted }}>{q.created_at ? new Date(q.created_at).toLocaleDateString("en-PK") : ""}</span>
                    </div>
                    <p style={{ margin: 0, fontSize: 15, color: C.textMid, lineHeight: 1.6, fontWeight: 500 }}>{q.question}</p>

                    {isLoading ? (
                      <div style={{ marginTop: 16, padding: "14px 18px", background: C.navyPale, borderRadius: 12, border: `1px solid #C7D8EC`, display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ fontSize: 20 }}>🤖</div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13, color: C.navy }}>Claude AI is thinking...</div>
                          <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>Generating answer</div>
                        </div>
                        <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
                          {[0,1,2].map(i => <div key={i} style={{ width: 7, height: 7, background: C.navy, borderRadius: "50%", animation: "pulse 1.2s infinite", animationDelay: `${i*0.3}s` }} />)}
                        </div>
                      </div>
                    ) : q.answer ? (
                      <div style={{ marginTop: 16, padding: "16px 18px", background: q.ai_answered ? C.navyPale : C.emeraldLight, borderRadius: 12, borderLeft: `3px solid ${q.ai_answered ? C.navy : "#10B981"}` }}>
                        <div style={{ fontWeight: 700, fontSize: 11, color: q.ai_answered ? C.navy : "#059669", marginBottom: 8 }}>
                          {q.ai_answered ? "🤖 Claude AI Answer" : "✅ Instructor Reply"}
                          <Badge type={q.ai_answered ? "info" : "success"} style={{ marginLeft: 8 }}>{q.ai_answered ? "AI" : "Manual"}</Badge>
                        </div>
                        <p style={{ margin: 0, fontSize: 14, color: C.textMid, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{q.answer}</p>
                        {q.answered_at && <div style={{ fontSize: 11, color: C.textMuted, marginTop: 8 }}>{new Date(q.answered_at).toLocaleDateString("en-PK")}</div>}
                      </div>
                    ) : (
                      <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 12 }}>
  <Badge type="warning">⏳ Awaiting reply</Badge>
  {isOwn && (
    <button style={S.btn("primary", "sm")} onClick={() => getAIReply(q)}>🤖 Get AI Answer</button>
  )}
</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })
      }

      {/* Ask Modal */}
      <Modal open={modal} onClose={() => setModal(false)} title="Ask a Question"
        footer={<><button style={S.btn("ghost")} onClick={() => setModal(false)}>Cancel</button><button style={S.btn("primary")} onClick={postQuestion}>Ask & Get AI Answer 🤖</button></>}>
        <Sel label="Course" value={form.courseId} onChange={e => setForm({ ...form, courseId: e.target.value })}>
          <option value="">Select course...</option>
          {courses.map(c => <option key={c.id} value={c.id}>{c.name} ({c.level})</option>)}
        </Sel>
        <Txtarea label="Your Question" placeholder="Ask anything about your course..." rows={4} value={form.question} onChange={e => setForm({ ...form, question: e.target.value })} />
        <div style={S.alert("info")}>🤖 AI will automatically answer your question!</div>

      </Modal>

      <style>{`@keyframes pulse{0%,100%{opacity:0.3;transform:scale(0.8)}50%{opacity:1;transform:scale(1.2)}}`}</style>
    </div>
  );
}